import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Server, Database, Shield, Network, Cloud, Cpu, HardDrive,
    ChevronRight, ChevronLeft, Check, Download, Mail,
    Building2, Globe, Lock, Zap, RefreshCw, AlertTriangle,
    Plus, Minus, ArrowRight, FileText, CheckCircle2,
    Layers, GitBranch, Activity, Eye, MemoryStick, Gauge,
    Router, Wifi, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useConsultationModal } from '../context/ConsultationModalContext';
import jsPDF from 'jspdf';

// Step configurations
const STEPS = [
    { id: 1, title: 'Requirements', icon: FileText },
    { id: 2, title: 'Infrastructure', icon: Server },
    { id: 3, title: 'Connectivity', icon: Network },
    { id: 4, title: 'Security & DR', icon: Shield },
    { id: 5, title: 'Review', icon: Eye }
];

// Compute options
const COMPUTE_TIERS = [
    { id: 'standard', name: 'Standard', vcpu: '2-8', ram: '4-32 GB', desc: 'General purpose workloads' },
    { id: 'compute', name: 'Compute Optimized', vcpu: '8-32', ram: '16-64 GB', desc: 'CPU-intensive applications' },
    { id: 'memory', name: 'Memory Optimized', vcpu: '4-16', ram: '64-256 GB', desc: 'In-memory databases, caching' },
    { id: 'gpu', name: 'GPU Accelerated', vcpu: '8-64', ram: '32-512 GB', desc: 'AI/ML, rendering workloads' }
];

// Storage options
const STORAGE_TYPES = [
    { id: 'nvme', name: 'NVMe SSD', iops: '100K+', desc: 'Ultra-high performance' },
    { id: 'ssd', name: 'Standard SSD', iops: '16K', desc: 'Balanced performance' },
    { id: 'hdd', name: 'Archive HDD', iops: '500', desc: 'Cold storage, backups' }
];

// Availability zones
const AVAILABILITY_ZONES = [
    { id: 'ktm', name: 'Kathmandu', code: 'AZ-1', role: 'Primary', color: 'emerald' },
    { id: 'ltp', name: 'Lalitpur', code: 'AZ-2', role: 'Near DR', color: 'amber' },
    { id: 'btl', name: 'Butwal', code: 'AZ-3', role: 'Far DR', color: 'blue' }
];

const Planner = () => {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';
    const { openModal } = useConsultationModal();
    const architectureRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showArchitecture, setShowArchitecture] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Form state
    const [config, setConfig] = useState({
        // Step 1: Requirements
        projectName: '',
        industry: '',
        workloadType: '',
        expectedUsers: '100',
        
        // Step 2: Infrastructure
        sizingMode: 'vm', // 'vm' or 'resource'
        computeTier: 'standard',
        vmCount: 2,
        // Resource-based sizing
        totalVcpu: 8,
        totalMemory: 32,
        totalStorage: 500,
        storageType: 'ssd',
        storageSize: 500,
        
        // Step 3: Connectivity
        hasOnPrem: false,
        onPremLocation: '',
        bandwidthRequired: '100',
        vpnType: 'ipsec',
        // Internet routing
        internetRouting: 'everestcloud', // 'everestcloud' or 'onprem'
        useFirewall: true,
        usePublicIP: true,
        
        // Step 4: Security & DR
        primaryZone: 'ktm',
        enableDR: true,
        drZone: 'ltp',
        backupFrequency: 'daily',
        retentionDays: 30,
        complianceNeeds: []
    });

    const updateConfig = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const nextStep = () => {
        if (currentStep < STEPS.length) {
            setCurrentStep(prev => prev + 1);
        }
        if (currentStep === STEPS.length - 1) {
            generateArchitecture();
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const generateArchitecture = () => {
        setIsGenerating(true);
        setTimeout(() => {
            setIsGenerating(false);
            setShowArchitecture(true);
        }, 2000);
    };

    // Professional PDF Generation
    const downloadPDF = async () => {
        setIsDownloading(true);
        
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const contentWidth = pageWidth - (margin * 2);
        
        const primaryZone = AVAILABILITY_ZONES.find(z => z.id === config.primaryZone);
        const drZone = AVAILABILITY_ZONES.find(z => z.id === config.drZone);
        const computeTier = COMPUTE_TIERS.find(t => t.id === config.computeTier);
        const storageType = STORAGE_TYPES.find(t => t.id === config.storageType);
        
        // Colors
        const amber = [245, 158, 11];
        const darkBg = [15, 23, 42];
        const white = [255, 255, 255];
        const gray = [148, 163, 184];
        const emerald = [16, 185, 129];
        
        // Page 1: Cover
        pdf.setFillColor(...darkBg);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        
        // Header accent bar
        pdf.setFillColor(...amber);
        pdf.rect(0, 0, pageWidth, 3, 'F');
        
        // Logo area
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin, 30, 50, 50, 5, 5, 'F');
        pdf.setTextColor(...amber);
        pdf.setFontSize(28);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EC', margin + 25, 62, { align: 'center' });
        
        // Title
        pdf.setTextColor(...white);
        pdf.setFontSize(32);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EverestCloud', margin + 60, 50);
        pdf.setTextColor(...amber);
        pdf.text('Architecture Proposal', margin + 60, 65);
        
        // Project name
        pdf.setTextColor(...white);
        pdf.setFontSize(18);
        pdf.text(config.projectName || 'Cloud Infrastructure Project', margin, 110);
        
        // Meta info box
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin, 125, contentWidth, 40, 3, 3, 'F');
        pdf.setTextColor(...gray);
        pdf.setFontSize(10);
        pdf.text('PREPARED FOR', margin + 10, 138);
        pdf.text('DATE', margin + 70, 138);
        pdf.text('REFERENCE', margin + 130, 138);
        pdf.setTextColor(...white);
        pdf.setFontSize(12);
        const industryNames = { banking: 'Banking & Finance', government: 'Government', telecom: 'Telecom & ISP', enterprise: 'Enterprise', healthcare: 'Healthcare', other: 'Other' };
        pdf.text(industryNames[config.industry] || 'Enterprise Client', margin + 10, 152);
        pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), margin + 70, 152);
        pdf.text(`EC-${Date.now().toString(36).toUpperCase()}`, margin + 130, 152);
        
        // Highlights
        let yPos = 185;
        pdf.setFillColor(...amber);
        pdf.roundedRect(margin, yPos, contentWidth / 3 - 5, 35, 3, 3, 'F');
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin + contentWidth / 3, yPos, contentWidth / 3 - 5, 35, 3, 3, 'F');
        pdf.roundedRect(margin + (contentWidth / 3) * 2, yPos, contentWidth / 3, 35, 3, 3, 'F');
        
        pdf.setTextColor(...darkBg);
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        if (config.sizingMode === 'vm') {
            pdf.text(`${config.vmCount}`, margin + (contentWidth / 6) - 2.5, yPos + 18, { align: 'center' });
        } else {
            pdf.text(`${config.totalVcpu}`, margin + (contentWidth / 6) - 2.5, yPos + 18, { align: 'center' });
        }
        pdf.setFontSize(9);
        pdf.text(config.sizingMode === 'vm' ? 'VIRTUAL MACHINES' : 'TOTAL vCPUs', margin + (contentWidth / 6) - 2.5, yPos + 28, { align: 'center' });
        
        pdf.setTextColor(...white);
        pdf.setFontSize(20);
        pdf.text(`${config.sizingMode === 'vm' ? config.storageSize : config.totalStorage}`, margin + (contentWidth / 2), yPos + 18, { align: 'center' });
        pdf.setFontSize(9);
        pdf.setTextColor(...gray);
        pdf.text('GB STORAGE', margin + (contentWidth / 2), yPos + 28, { align: 'center' });
        
        pdf.setTextColor(...white);
        pdf.setFontSize(20);
        pdf.text(config.enableDR ? '2' : '1', margin + (contentWidth / 6) * 5, yPos + 18, { align: 'center' });
        pdf.setFontSize(9);
        pdf.setTextColor(...gray);
        pdf.text('AVAILABILITY ZONES', margin + (contentWidth / 6) * 5, yPos + 28, { align: 'center' });
        
        // Footer
        pdf.setTextColor(...gray);
        pdf.setFontSize(8);
        pdf.text('Nepal\'s Sovereign Enterprise Cloud | www.everestcloud.com.np', pageWidth / 2, pageHeight - 15, { align: 'center' });
        pdf.setFillColor(...amber);
        pdf.rect(0, pageHeight - 3, pageWidth, 3, 'F');
        
        // Page 2: Architecture
        pdf.addPage();
        pdf.setFillColor(...darkBg);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(...amber);
        pdf.rect(0, 0, pageWidth, 3, 'F');
        
        // Header
        pdf.setTextColor(...amber);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Architecture Overview', margin, 25);
        pdf.setDrawColor(...amber);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 30, margin + 60, 30);
        
        // Infrastructure section
        yPos = 45;
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin, yPos, contentWidth, 55, 3, 3, 'F');
        
        pdf.setTextColor(...gray);
        pdf.setFontSize(10);
        pdf.text('COMPUTE CONFIGURATION', margin + 10, yPos + 15);
        
        pdf.setTextColor(...white);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        if (config.sizingMode === 'vm') {
            pdf.text(`${config.vmCount} x ${computeTier?.name || 'Standard'} VMs`, margin + 10, yPos + 30);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(...gray);
            pdf.text(`vCPU: ${computeTier?.vcpu || '2-8'} cores per VM | RAM: ${computeTier?.ram || '4-32 GB'} per VM`, margin + 10, yPos + 42);
        } else {
            pdf.text(`${config.totalVcpu} vCPUs | ${config.totalMemory} GB RAM`, margin + 10, yPos + 30);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(...gray);
            pdf.text(`Resource-based allocation | Flexible VM distribution`, margin + 10, yPos + 42);
        }
        
        // Storage box
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin + contentWidth * 0.6, yPos + 10, contentWidth * 0.35, 35, 3, 3, 'F');
        pdf.setTextColor(...amber);
        pdf.setFontSize(9);
        pdf.text('STORAGE', margin + contentWidth * 0.65, yPos + 22);
        pdf.setTextColor(...white);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${config.sizingMode === 'vm' ? config.storageSize : config.totalStorage} GB`, margin + contentWidth * 0.65, yPos + 35);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...gray);
        pdf.text(`${storageType?.name || 'SSD'} | ${storageType?.iops || '16K'} IOPS`, margin + contentWidth * 0.65, yPos + 43);
        
        // Zones section
        yPos = 115;
        pdf.setTextColor(...amber);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DEPLOYMENT TOPOLOGY', margin, yPos);
        
        yPos += 15;
        // Primary zone
        pdf.setFillColor(16, 185, 129, 30);
        pdf.roundedRect(margin, yPos, config.enableDR ? contentWidth * 0.47 : contentWidth, 50, 3, 3, 'F');
        pdf.setDrawColor(...emerald);
        pdf.setLineWidth(1);
        pdf.roundedRect(margin, yPos, config.enableDR ? contentWidth * 0.47 : contentWidth, 50, 3, 3, 'S');
        
        pdf.setFillColor(...emerald);
        pdf.circle(margin + 10, yPos + 12, 3, 'F');
        pdf.setTextColor(...emerald);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${primaryZone?.name || 'Kathmandu'} (${primaryZone?.code || 'AZ-1'})`, margin + 18, yPos + 15);
        pdf.setFillColor(...emerald);
        pdf.roundedRect(margin + 100, yPos + 8, 30, 10, 2, 2, 'F');
        pdf.setTextColor(...white);
        pdf.setFontSize(7);
        pdf.text('PRIMARY', margin + 115, yPos + 15, { align: 'center' });
        
        pdf.setTextColor(...white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Active workloads | Real-time processing', margin + 10, yPos + 32);
        pdf.setTextColor(...gray);
        pdf.setFontSize(9);
        pdf.text(`${config.sizingMode === 'vm' ? config.vmCount + ' VMs' : config.totalVcpu + ' vCPUs'} | ${config.sizingMode === 'vm' ? config.storageSize : config.totalStorage} GB Storage`, margin + 10, yPos + 43);
        
        // DR zone
        if (config.enableDR) {
            pdf.setFillColor(245, 158, 11, 30);
            pdf.roundedRect(margin + contentWidth * 0.53, yPos, contentWidth * 0.47, 50, 3, 3, 'F');
            pdf.setDrawColor(...amber);
            pdf.roundedRect(margin + contentWidth * 0.53, yPos, contentWidth * 0.47, 50, 3, 3, 'S');
            
            pdf.setFillColor(...amber);
            pdf.circle(margin + contentWidth * 0.53 + 10, yPos + 12, 3, 'F');
            pdf.setTextColor(...amber);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${drZone?.name || 'Lalitpur'} (${drZone?.code || 'AZ-2'})`, margin + contentWidth * 0.53 + 18, yPos + 15);
            pdf.setFillColor(...amber);
            pdf.roundedRect(margin + contentWidth * 0.53 + 80, yPos + 8, 25, 10, 2, 2, 'F');
            pdf.setTextColor(...darkBg);
            pdf.setFontSize(7);
            pdf.text('DR SITE', margin + contentWidth * 0.53 + 92.5, yPos + 15, { align: 'center' });
            
            pdf.setTextColor(...white);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text('Standby | Synchronous replication', margin + contentWidth * 0.53 + 10, yPos + 32);
            pdf.setTextColor(...gray);
            pdf.setFontSize(9);
            pdf.text('RPO=0 | RTO < 15 minutes', margin + contentWidth * 0.53 + 10, yPos + 43);
        }
        
        // Connectivity section
        yPos = 195;
        pdf.setTextColor(...amber);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('NETWORK CONNECTIVITY', margin, yPos);
        
        yPos += 15;
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin, yPos, contentWidth, config.hasOnPrem ? 70 : 45, 3, 3, 'F');
        
        // Internet routing
        pdf.setTextColor(...gray);
        pdf.setFontSize(9);
        pdf.text('INTERNET ROUTING', margin + 10, yPos + 12);
        pdf.setTextColor(...white);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        if (config.internetRouting === 'everestcloud') {
            pdf.text('EverestCloud Direct Internet', margin + 10, yPos + 25);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(...gray);
            const features = [];
            if (config.usePublicIP) features.push('Public IP');
            if (config.useFirewall) features.push('Managed Firewall');
            features.push('DDoS Protection');
            pdf.text(features.join(' | '), margin + 10, yPos + 35);
        } else {
            pdf.text('Routed via On-Premises', margin + 10, yPos + 25);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(...gray);
            pdf.text('Traffic egress through customer datacenter | Existing firewall/security', margin + 10, yPos + 35);
        }
        
        if (config.hasOnPrem) {
            pdf.setTextColor(...gray);
            pdf.setFontSize(9);
            pdf.text('ON-PREMISES CONNECTION', margin + 10, yPos + 50);
            pdf.setTextColor(...white);
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.text(config.onPremLocation || 'Customer Datacenter', margin + 10, yPos + 62);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(...gray);
            pdf.text(`${config.vpnType.toUpperCase()} VPN | ${config.bandwidthRequired} Mbps | Encrypted tunnel`, margin + 10, yPos + 72);
        }
        
        // Footer
        pdf.setTextColor(...gray);
        pdf.setFontSize(8);
        pdf.text('Nepal\'s Sovereign Enterprise Cloud | www.everestcloud.com.np', pageWidth / 2, pageHeight - 15, { align: 'center' });
        pdf.setFillColor(...amber);
        pdf.rect(0, pageHeight - 3, pageWidth, 3, 'F');
        
        // Page 3: Recommendations & Next Steps
        pdf.addPage();
        pdf.setFillColor(...darkBg);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(...amber);
        pdf.rect(0, 0, pageWidth, 3, 'F');
        
        // Header
        pdf.setTextColor(...amber);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Recommendations', margin, 25);
        pdf.setDrawColor(...amber);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 30, margin + 55, 30);
        
        // Recommendations list
        yPos = 45;
        const recommendations = [
            { title: 'High Availability', desc: 'Multi-AZ deployment ensures 99.99% uptime SLA with automatic failover capabilities' },
            { title: 'Data Protection', desc: `${config.backupFrequency.charAt(0).toUpperCase() + config.backupFrequency.slice(1)} backups with ${config.retentionDays}-day retention ensures business continuity` },
            { title: 'Security', desc: 'Enterprise-grade security with encryption at rest and in transit, compliant with industry standards' },
            { title: 'Performance', desc: `${storageType?.name || 'SSD'} storage with ${storageType?.iops || '16K'} IOPS ensures optimal application performance` }
        ];
        
        if (config.enableDR) {
            recommendations.push({ title: 'Disaster Recovery', desc: 'Synchronous replication to secondary zone with RPO=0 and RTO < 15 minutes' });
        }
        if (config.hasOnPrem) {
            recommendations.push({ title: 'Hybrid Connectivity', desc: `Secure ${config.vpnType.toUpperCase()} connection with ${config.bandwidthRequired} Mbps dedicated bandwidth` });
        }
        if ((config.complianceNeeds || []).length > 0) {
            recommendations.push({ title: 'Compliance', desc: `Infrastructure configured for ${config.complianceNeeds.join(', ').toUpperCase()} compliance requirements` });
        }
        
        recommendations.forEach((rec, i) => {
            pdf.setFillColor(30, 41, 59);
            pdf.roundedRect(margin, yPos, contentWidth, 28, 3, 3, 'F');
            
            pdf.setFillColor(...emerald);
            pdf.circle(margin + 10, yPos + 14, 4, 'F');
            pdf.setTextColor(...white);
            pdf.setFontSize(8);
            pdf.text('✓', margin + 8, yPos + 17);
            
            pdf.setTextColor(...white);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(rec.title, margin + 22, yPos + 12);
            
            pdf.setTextColor(...gray);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.text(rec.desc, margin + 22, yPos + 22);
            
            yPos += 33;
        });
        
        // Next Steps
        yPos += 10;
        pdf.setTextColor(...amber);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Next Steps', margin, yPos);
        
        yPos += 12;
        pdf.setFillColor(...amber);
        pdf.roundedRect(margin, yPos, contentWidth, 40, 3, 3, 'F');
        
        pdf.setTextColor(...darkBg);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Ready to proceed?', margin + 10, yPos + 15);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text('Contact our solutions team for detailed pricing and implementation timeline.', margin + 10, yPos + 28);
        
        // Contact info
        yPos += 55;
        pdf.setFillColor(30, 41, 59);
        pdf.roundedRect(margin, yPos, contentWidth, 35, 3, 3, 'F');
        
        pdf.setTextColor(...white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Contact Us', margin + 10, yPos + 12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...gray);
        pdf.setFontSize(9);
        pdf.text('Email: sales@digitalnetwork.com.np', margin + 10, yPos + 22);
        pdf.text('Phone: +977-14537311', margin + 10, yPos + 30);
        pdf.text('Web: www.everestcloud.com.np', margin + 100, yPos + 22);
        
        // Footer
        pdf.setTextColor(...gray);
        pdf.setFontSize(8);
        pdf.text('Nepal\'s Sovereign Enterprise Cloud | www.everestcloud.com.np', pageWidth / 2, pageHeight - 15, { align: 'center' });
        pdf.setFillColor(...amber);
        pdf.rect(0, pageHeight - 3, pageWidth, 3, 'F');
        
        // Save
        pdf.save(`EverestCloud-Proposal-${config.projectName || 'Project'}.pdf`);
        setIsDownloading(false);
    };

    // Render step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return <RequirementsStep config={config} updateConfig={updateConfig} isDarkMode={isDarkMode} />;
            case 2:
                return <InfrastructureStep config={config} updateConfig={updateConfig} isDarkMode={isDarkMode} />;
            case 3:
                return <ConnectivityStep config={config} updateConfig={updateConfig} isDarkMode={isDarkMode} />;
            case 4:
                return <SecurityDRStep config={config} updateConfig={updateConfig} isDarkMode={isDarkMode} />;
            case 5:
                return <ReviewStep config={config} isDarkMode={isDarkMode} showArchitecture={showArchitecture} isGenerating={isGenerating} architectureRef={architectureRef} />;
            default:
                return null;
        }
    };

    return (
        <Layout>
            <SEO page="planner" />
            <div className={`min-h-screen pt-24 pb-16 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6"
                        >
                            <Cpu className="w-4 h-4 text-amber-500" />
                            <span className="text-amber-500 text-sm font-medium">Cloud Architecture Designer</span>
                        </motion.div>
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ delay: 0.1 }}
                            className={`text-4xl md:text-5xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                        >
                            EverestCloud <span className="text-amber-500">Planner</span>
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            transition={{ delay: 0.2 }}
                            className={`text-lg max-w-2xl mx-auto ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}
                        >
                            Design your cloud architecture, connect your on-premises infrastructure, and get personalized recommendations.
                        </motion.p>
                    </div>

                    {/* Progress Steps */}
                    <div className="max-w-4xl mx-auto mb-10">
                        <div className="flex items-center justify-between">
                            {STEPS.map((step, index) => (
                                <React.Fragment key={step.id}>
                                    <div className="flex flex-col items-center">
                                        <div 
                                            className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                                                currentStep >= step.id 
                                                    ? 'bg-amber-500 text-slate-900' 
                                                    : isDarkMode ? 'bg-white/5 text-slate-500 border border-white/10' : 'bg-slate-200 text-slate-400'
                                            }`}
                                        >
                                            {currentStep > step.id ? (
                                                <Check className="w-5 h-5" />
                                            ) : (
                                                <step.icon className="w-5 h-5" />
                                            )}
                                        </div>
                                        <span className={`text-xs mt-2 font-medium ${
                                            currentStep >= step.id 
                                                ? (isDarkMode ? 'text-white' : 'text-slate-900')
                                                : (isDarkMode ? 'text-slate-500' : 'text-slate-400')
                                        }`}>
                                            {step.title}
                                        </span>
                                    </div>
                                    {index < STEPS.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 ${
                                            currentStep > step.id 
                                                ? 'bg-amber-500' 
                                                : isDarkMode ? 'bg-white/10' : 'bg-slate-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Main Content Card */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className={`max-w-4xl mx-auto rounded-2xl border ${
                            isDarkMode ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200'
                        } p-8`}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {renderStepContent()}
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200 dark:border-white/10">
                            <Button
                                variant="outline"
                                onClick={prevStep}
                                disabled={currentStep === 1}
                                className={`${currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <ChevronLeft className="w-4 h-4 mr-2" />
                                Previous
                            </Button>

                            {currentStep === STEPS.length ? (
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={downloadPDF}
                                        disabled={isDownloading}
                                        className="gap-2"
                                    >
                                        {isDownloading ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Download className="w-4 h-4" />
                                        )}
                                        {isDownloading ? 'Generating...' : 'Download PDF'}
                                    </Button>
                                    <Button
                                        onClick={openModal}
                                        className="bg-amber-500 hover:bg-amber-600 text-slate-900 gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Request Quote
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    onClick={nextStep}
                                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                                >
                                    {currentStep === STEPS.length - 1 ? 'Generate Architecture' : 'Next'}
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </Layout>
    );
};

// Step 1: Requirements
const RequirementsStep = ({ config, updateConfig, isDarkMode }) => {
    const industries = [
        { id: 'banking', name: 'Banking & Finance', icon: Building2 },
        { id: 'government', name: 'Government', icon: Layers },
        { id: 'telecom', name: 'Telecom & ISP', icon: Globe },
        { id: 'enterprise', name: 'Enterprise', icon: Building2 },
        { id: 'healthcare', name: 'Healthcare', icon: Activity },
        { id: 'other', name: 'Other', icon: Cpu }
    ];

    const workloads = [
        { id: 'web', name: 'Web Applications', desc: 'Websites, APIs, microservices' },
        { id: 'database', name: 'Databases', desc: 'SQL, NoSQL, data warehouses' },
        { id: 'analytics', name: 'Analytics & BI', desc: 'Data processing, reporting' },
        { id: 'aiml', name: 'AI/ML Workloads', desc: 'Training, inference, GPUs' },
        { id: 'erp', name: 'ERP/CRM Systems', desc: 'Business applications' },
        { id: 'backup', name: 'Backup & DR', desc: 'Disaster recovery site' }
    ];

    return (
        <div className="space-y-8">
            <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Project Requirements
                </h2>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Tell us about your project and workload requirements.
                </p>
            </div>

            {/* Project Name */}
            <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Project Name
                </label>
                <input
                    type="text"
                    value={config.projectName}
                    onChange={(e) => updateConfig('projectName', e.target.value)}
                    placeholder="e.g., Core Banking Migration"
                    className={`w-full px-4 py-3 rounded-xl border ${
                        isDarkMode 
                            ? 'bg-white/5 border-white/10 text-white placeholder-slate-500' 
                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                />
            </div>

            {/* Industry Selection */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Industry Sector
                </label>
                <div className="grid grid-cols-3 gap-3">
                    {industries.map((ind) => (
                        <button
                            key={ind.id}
                            onClick={() => updateConfig('industry', ind.id)}
                            className={`p-4 rounded-xl border text-left transition-all ${
                                config.industry === ind.id
                                    ? 'bg-amber-500/10 border-amber-500 text-amber-500'
                                    : isDarkMode 
                                        ? 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20' 
                                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                            }`}
                        >
                            <ind.icon className="w-5 h-5 mb-2" />
                            <span className="text-sm font-medium">{ind.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Workload Type */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Primary Workload Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                    {workloads.map((wl) => (
                        <button
                            key={wl.id}
                            onClick={() => updateConfig('workloadType', wl.id)}
                            className={`p-4 rounded-xl border text-left transition-all ${
                                config.workloadType === wl.id
                                    ? 'bg-amber-500/10 border-amber-500'
                                    : isDarkMode 
                                        ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <span className={`font-medium ${config.workloadType === wl.id ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                {wl.name}
                            </span>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{wl.desc}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Expected Users */}
            <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Expected Concurrent Users
                </label>
                <select
                    value={config.expectedUsers}
                    onChange={(e) => updateConfig('expectedUsers', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${
                        isDarkMode 
                            ? 'bg-white/5 border-white/10 text-white' 
                            : 'bg-white border-slate-200 text-slate-900'
                    } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                >
                    <option value="100">Up to 100 users</option>
                    <option value="500">100 - 500 users</option>
                    <option value="1000">500 - 1,000 users</option>
                    <option value="5000">1,000 - 5,000 users</option>
                    <option value="10000">5,000+ users</option>
                </select>
            </div>
        </div>
    );
};

// Step 2: Infrastructure
const InfrastructureStep = ({ config, updateConfig, isDarkMode }) => {
    return (
        <div className="space-y-8">
            <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Infrastructure Configuration
                </h2>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Configure your compute and storage requirements.
                </p>
            </div>

            {/* Sizing Mode Toggle */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    How would you like to size your infrastructure?
                </label>
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => updateConfig('sizingMode', 'vm')}
                        className={`p-5 rounded-xl border text-left transition-all ${
                            config.sizingMode === 'vm'
                                ? 'bg-amber-500/10 border-amber-500'
                                : isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <Server className={`w-5 h-5 ${config.sizingMode === 'vm' ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                            <span className={`font-bold ${config.sizingMode === 'vm' ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                VM-Based Sizing
                            </span>
                            {config.sizingMode === 'vm' && <CheckCircle2 className="w-5 h-5 text-amber-500 ml-auto" />}
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Specify number of VMs and select compute tier
                        </p>
                    </button>
                    <button
                        onClick={() => updateConfig('sizingMode', 'resource')}
                        className={`p-5 rounded-xl border text-left transition-all ${
                            config.sizingMode === 'resource'
                                ? 'bg-amber-500/10 border-amber-500'
                                : isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <Gauge className={`w-5 h-5 ${config.sizingMode === 'resource' ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                            <span className={`font-bold ${config.sizingMode === 'resource' ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                Resource-Based Sizing
                            </span>
                            {config.sizingMode === 'resource' && <CheckCircle2 className="w-5 h-5 text-amber-500 ml-auto" />}
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Specify total vCPU, Memory, and Storage
                        </p>
                    </button>
                </div>
            </div>

            {/* VM-Based Sizing */}
            {config.sizingMode === 'vm' && (
                <>
                    {/* Compute Tier */}
                    <div>
                        <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            Compute Tier
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                            {COMPUTE_TIERS.map((tier) => (
                                <button
                                    key={tier.id}
                                    onClick={() => updateConfig('computeTier', tier.id)}
                                    className={`p-5 rounded-xl border text-left transition-all ${
                                        config.computeTier === tier.id
                                            ? 'bg-amber-500/10 border-amber-500'
                                            : isDarkMode 
                                                ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                                : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`font-bold ${config.computeTier === tier.id ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                            {tier.name}
                                        </span>
                                        {config.computeTier === tier.id && <CheckCircle2 className="w-5 h-5 text-amber-500" />}
                                    </div>
                                    <div className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        <p>vCPU: {tier.vcpu} cores</p>
                                        <p>RAM: {tier.ram}</p>
                                        <p className="mt-2 text-slate-500">{tier.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* VM Count */}
                    <div>
                        <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            Number of Virtual Machines
                        </label>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => updateConfig('vmCount', Math.max(1, config.vmCount - 1))}
                                className={`p-3 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-slate-100 border-slate-200 hover:bg-slate-200'}`}
                            >
                                <Minus className="w-5 h-5" />
                            </button>
                            <div className={`flex-1 text-center py-3 px-6 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{config.vmCount}</span>
                                <span className={`text-sm ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>VMs</span>
                            </div>
                            <button
                                onClick={() => updateConfig('vmCount', Math.min(50, config.vmCount + 1))}
                                className={`p-3 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-slate-100 border-slate-200 hover:bg-slate-200'}`}
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Resource-Based Sizing */}
            {config.sizingMode === 'resource' && (
                <div className="space-y-6">
                    {/* vCPU */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <div className="flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-amber-500" />
                                Total vCPUs: <span className="text-amber-500 font-bold">{config.totalVcpu}</span>
                            </div>
                        </label>
                        <input
                            type="range"
                            min="2"
                            max="256"
                            step="2"
                            value={config.totalVcpu}
                            onChange={(e) => updateConfig('totalVcpu', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                        />
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                            <span>2 vCPUs</span>
                            <span>256 vCPUs</span>
                        </div>
                    </div>

                    {/* Memory */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <div className="flex items-center gap-2">
                                <MemoryStick className="w-4 h-4 text-amber-500" />
                                Total Memory: <span className="text-amber-500 font-bold">{config.totalMemory} GB</span>
                            </div>
                        </label>
                        <input
                            type="range"
                            min="4"
                            max="1024"
                            step="4"
                            value={config.totalMemory}
                            onChange={(e) => updateConfig('totalMemory', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                        />
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                            <span>4 GB</span>
                            <span>1 TB</span>
                        </div>
                    </div>

                    {/* Storage for resource mode */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <div className="flex items-center gap-2">
                                <HardDrive className="w-4 h-4 text-amber-500" />
                                Total Storage: <span className="text-amber-500 font-bold">{config.totalStorage} GB</span>
                            </div>
                        </label>
                        <input
                            type="range"
                            min="100"
                            max="10000"
                            step="100"
                            value={config.totalStorage}
                            onChange={(e) => updateConfig('totalStorage', parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                        />
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                            <span>100 GB</span>
                            <span>10 TB</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Storage Type */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Storage Type
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {STORAGE_TYPES.map((storage) => (
                        <button
                            key={storage.id}
                            onClick={() => updateConfig('storageType', storage.id)}
                            className={`p-4 rounded-xl border text-center transition-all ${
                                config.storageType === storage.id
                                    ? 'bg-amber-500/10 border-amber-500'
                                    : isDarkMode 
                                        ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <HardDrive className={`w-6 h-6 mx-auto mb-2 ${config.storageType === storage.id ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                            <span className={`font-medium block ${config.storageType === storage.id ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                {storage.name}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{storage.iops} IOPS</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Storage Size (VM mode only) */}
            {config.sizingMode === 'vm' && (
                <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Storage Capacity: <span className="text-amber-500 font-bold">{config.storageSize} GB</span>
                    </label>
                    <input
                        type="range"
                        min="100"
                        max="10000"
                        step="100"
                        value={config.storageSize}
                        onChange={(e) => updateConfig('storageSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                    />
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                        <span>100 GB</span>
                        <span>10 TB</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// Step 3: Connectivity
const ConnectivityStep = ({ config, updateConfig, isDarkMode }) => {
    return (
        <div className="space-y-8">
            <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Network & Connectivity
                </h2>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Configure your network connectivity and internet routing preferences.
                </p>
            </div>

            {/* Internet Routing Options */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Internet Traffic Routing
                </label>
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => updateConfig('internetRouting', 'everestcloud')}
                        className={`p-5 rounded-xl border text-left transition-all ${
                            config.internetRouting === 'everestcloud'
                                ? 'bg-amber-500/10 border-amber-500'
                                : isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.internetRouting === 'everestcloud' ? 'bg-amber-500/20' : (isDarkMode ? 'bg-white/10' : 'bg-slate-200')}`}>
                                <Cloud className={`w-5 h-5 ${config.internetRouting === 'everestcloud' ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                            </div>
                            <div>
                                <span className={`font-bold block ${config.internetRouting === 'everestcloud' ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                    EverestCloud Internet
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Direct cloud egress</span>
                            </div>
                            {config.internetRouting === 'everestcloud' && <CheckCircle2 className="w-5 h-5 text-amber-500 ml-auto" />}
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Use EverestCloud's public IP and managed firewall services for internet access
                        </p>
                    </button>
                    <button
                        onClick={() => {
                            updateConfig('internetRouting', 'onprem');
                            if (!config.hasOnPrem) updateConfig('hasOnPrem', true);
                        }}
                        className={`p-5 rounded-xl border text-left transition-all ${
                            config.internetRouting === 'onprem'
                                ? 'bg-amber-500/10 border-amber-500'
                                : isDarkMode 
                                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.internetRouting === 'onprem' ? 'bg-amber-500/20' : (isDarkMode ? 'bg-white/10' : 'bg-slate-200')}`}>
                                <Router className={`w-5 h-5 ${config.internetRouting === 'onprem' ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                            </div>
                            <div>
                                <span className={`font-bold block ${config.internetRouting === 'onprem' ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                    Route via On-Prem
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Backhaul through datacenter</span>
                            </div>
                            {config.internetRouting === 'onprem' && <CheckCircle2 className="w-5 h-5 text-amber-500 ml-auto" />}
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Route all internet traffic through your on-premises datacenter and existing security infrastructure
                        </p>
                    </button>
                </div>
            </div>

            {/* EverestCloud Internet Options */}
            {config.internetRouting === 'everestcloud' && (
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <h4 className={`font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>EverestCloud Services</h4>
                    <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={config.usePublicIP}
                                onChange={(e) => updateConfig('usePublicIP', e.target.checked)}
                                className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                            />
                            <div className="flex items-center gap-2">
                                <Wifi className={`w-4 h-4 ${config.usePublicIP ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                                <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Public IP Address</span>
                            </div>
                            <span className={`text-xs ml-auto ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Dedicated static IP</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={config.useFirewall}
                                onChange={(e) => updateConfig('useFirewall', e.target.checked)}
                                className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                            />
                            <div className="flex items-center gap-2">
                                <ShieldCheck className={`w-4 h-4 ${config.useFirewall ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                                <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Managed Firewall</span>
                            </div>
                            <span className={`text-xs ml-auto ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>L7 WAF + DDoS protection</span>
                        </label>
                    </div>
                </div>
            )}

            {/* On-Prem Toggle */}
            <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.hasOnPrem ? 'bg-amber-500/20' : (isDarkMode ? 'bg-white/10' : 'bg-slate-200')}`}>
                            <Building2 className={`w-6 h-6 ${config.hasOnPrem ? 'text-amber-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                        </div>
                        <div>
                            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Connect On-Premises Datacenter</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Hybrid cloud with secure VPN connectivity</p>
                        </div>
                    </div>
                    <button
                        onClick={() => updateConfig('hasOnPrem', !config.hasOnPrem)}
                        disabled={config.internetRouting === 'onprem'}
                        className={`relative w-14 h-7 rounded-full transition-all ${config.hasOnPrem ? 'bg-amber-500' : (isDarkMode ? 'bg-white/20' : 'bg-slate-300')} ${config.internetRouting === 'onprem' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${config.hasOnPrem ? 'left-8' : 'left-1'}`} />
                    </button>
                </div>

                {config.hasOnPrem && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10 space-y-4"
                    >
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                On-Premises Location
                            </label>
                            <input
                                type="text"
                                value={config.onPremLocation}
                                onChange={(e) => updateConfig('onPremLocation', e.target.value)}
                                placeholder="e.g., Kathmandu Head Office"
                                className={`w-full px-4 py-3 rounded-xl border ${
                                    isDarkMode 
                                        ? 'bg-white/5 border-white/10 text-white placeholder-slate-500' 
                                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                                } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                            />
                        </div>

                        <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Required Bandwidth
                            </label>
                            <select
                                value={config.bandwidthRequired}
                                onChange={(e) => updateConfig('bandwidthRequired', e.target.value)}
                                className={`w-full px-4 py-3 rounded-xl border ${
                                    isDarkMode 
                                        ? 'bg-white/5 border-white/10 text-white' 
                                        : 'bg-white border-slate-200 text-slate-900'
                                } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                            >
                                <option value="100">100 Mbps</option>
                                <option value="500">500 Mbps</option>
                                <option value="1000">1 Gbps</option>
                                <option value="10000">10 Gbps</option>
                            </select>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                VPN Type
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { id: 'ipsec', name: 'IPSec VPN', desc: 'Industry standard, high security' },
                                    { id: 'mpls', name: 'MPLS Link', desc: 'Dedicated, guaranteed bandwidth' }
                                ].map((vpn) => (
                                    <button
                                        key={vpn.id}
                                        onClick={() => updateConfig('vpnType', vpn.id)}
                                        className={`p-4 rounded-xl border text-left transition-all ${
                                            config.vpnType === vpn.id
                                                ? 'bg-amber-500/10 border-amber-500'
                                                : isDarkMode 
                                                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                                    : 'bg-white border-slate-200 hover:border-slate-300'
                                        }`}
                                    >
                                        <span className={`font-medium ${config.vpnType === vpn.id ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                            {vpn.name}
                                        </span>
                                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{vpn.desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Connection Diagram Preview */}
            <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <h3 className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Connection Preview</h3>
                <div className="flex items-center justify-center gap-4">
                    {config.hasOnPrem && (
                        <>
                            <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                <Building2 className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>On-Premises</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-12 h-0.5 ${isDarkMode ? 'bg-amber-500/50' : 'bg-amber-500'}`}></div>
                                <Lock className="w-4 h-4 text-amber-500" />
                                <div className={`w-12 h-0.5 ${isDarkMode ? 'bg-amber-500/50' : 'bg-amber-500'}`}></div>
                            </div>
                        </>
                    )}
                    <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                        <Cloud className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                        <span className={`text-xs text-emerald-600 dark:text-emerald-400`}>EverestCloud</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`w-12 h-0.5 ${config.internetRouting === 'onprem' ? (isDarkMode ? 'bg-slate-600' : 'bg-slate-300') : (isDarkMode ? 'bg-amber-500/50' : 'bg-amber-500')}`}></div>
                        {config.internetRouting === 'everestcloud' ? (
                            <ShieldCheck className="w-4 h-4 text-amber-500" />
                        ) : (
                            <Router className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        )}
                        <div className={`w-12 h-0.5 ${config.internetRouting === 'onprem' ? (isDarkMode ? 'bg-slate-600' : 'bg-slate-300') : (isDarkMode ? 'bg-amber-500/50' : 'bg-amber-500')}`}></div>
                    </div>
                    <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                        <Globe className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Internet</span>
                    </div>
                </div>
                {config.internetRouting === 'onprem' && (
                    <p className={`text-center text-xs mt-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Internet traffic routes through your on-premises infrastructure
                    </p>
                )}
            </div>
        </div>
    );
};

// Step 4: Security & DR
const SecurityDRStep = ({ config, updateConfig, isDarkMode }) => {
    const complianceOptions = [
        { id: 'nrb', name: 'NRB Guidelines', desc: 'Nepal Rastra Bank compliance' },
        { id: 'iso27001', name: 'ISO 27001', desc: 'Information security management' },
        { id: 'pci', name: 'PCI DSS', desc: 'Payment card industry standard' },
        { id: 'hipaa', name: 'Healthcare', desc: 'Health data protection' }
    ];

    const toggleCompliance = (id) => {
        const current = config.complianceNeeds || [];
        if (current.includes(id)) {
            updateConfig('complianceNeeds', current.filter(c => c !== id));
        } else {
            updateConfig('complianceNeeds', [...current, id]);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Security & Disaster Recovery
                </h2>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Configure high availability, disaster recovery, and compliance requirements.
                </p>
            </div>

            {/* Primary Zone */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Primary Availability Zone
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {AVAILABILITY_ZONES.map((zone) => (
                        <button
                            key={zone.id}
                            onClick={() => updateConfig('primaryZone', zone.id)}
                            className={`p-4 rounded-xl border text-center transition-all ${
                                config.primaryZone === zone.id
                                    ? `bg-${zone.color}-500/10 border-${zone.color}-500`
                                    : isDarkMode 
                                        ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <span className={`font-bold block ${config.primaryZone === zone.id ? `text-${zone.color}-500` : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                {zone.name}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{zone.code} • {zone.role}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* DR Configuration */}
            <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.enableDR ? 'bg-emerald-500/20' : (isDarkMode ? 'bg-white/10' : 'bg-slate-200')}`}>
                            <RefreshCw className={`w-6 h-6 ${config.enableDR ? 'text-emerald-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                        </div>
                        <div>
                            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Enable Disaster Recovery</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Replicate to secondary zone for business continuity</p>
                        </div>
                    </div>
                    <button
                        onClick={() => updateConfig('enableDR', !config.enableDR)}
                        className={`relative w-14 h-7 rounded-full transition-all ${config.enableDR ? 'bg-emerald-500' : (isDarkMode ? 'bg-white/20' : 'bg-slate-300')}`}
                    >
                        <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${config.enableDR ? 'left-8' : 'left-1'}`} />
                    </button>
                </div>

                {config.enableDR && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10">
                        <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            DR Zone
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                            {AVAILABILITY_ZONES.filter(z => z.id !== config.primaryZone).map((zone) => (
                                <button
                                    key={zone.id}
                                    onClick={() => updateConfig('drZone', zone.id)}
                                    className={`p-4 rounded-xl border text-center transition-all ${
                                        config.drZone === zone.id
                                            ? 'bg-amber-500/10 border-amber-500'
                                            : isDarkMode 
                                                ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                                : 'bg-white border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    <span className={`font-medium ${config.drZone === zone.id ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                        {zone.name}
                                    </span>
                                    <span className={`text-xs block ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{zone.code}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Backup Settings */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Backup Frequency
                    </label>
                    <select
                        value={config.backupFrequency}
                        onChange={(e) => updateConfig('backupFrequency', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border ${
                            isDarkMode 
                                ? 'bg-white/5 border-white/10 text-white' 
                                : 'bg-white border-slate-200 text-slate-900'
                        } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                    >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                    </select>
                </div>
                <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Retention Period
                    </label>
                    <select
                        value={config.retentionDays}
                        onChange={(e) => updateConfig('retentionDays', parseInt(e.target.value))}
                        className={`w-full px-4 py-3 rounded-xl border ${
                            isDarkMode 
                                ? 'bg-white/5 border-white/10 text-white' 
                                : 'bg-white border-slate-200 text-slate-900'
                        } focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                    >
                        <option value="7">7 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                        <option value="365">1 year</option>
                    </select>
                </div>
            </div>

            {/* Compliance Requirements */}
            <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Compliance Requirements
                </label>
                <div className="grid grid-cols-2 gap-3">
                    {complianceOptions.map((opt) => (
                        <button
                            key={opt.id}
                            onClick={() => toggleCompliance(opt.id)}
                            className={`p-4 rounded-xl border text-left transition-all ${
                                (config.complianceNeeds || []).includes(opt.id)
                                    ? 'bg-amber-500/10 border-amber-500'
                                    : isDarkMode 
                                        ? 'bg-white/5 border-white/10 hover:border-white/20' 
                                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                                    (config.complianceNeeds || []).includes(opt.id)
                                        ? 'bg-amber-500 border-amber-500'
                                        : isDarkMode ? 'border-white/20' : 'border-slate-300'
                                }`}>
                                    {(config.complianceNeeds || []).includes(opt.id) && <Check className="w-3 h-3 text-slate-900" />}
                                </div>
                                <span className={`font-medium ${(config.complianceNeeds || []).includes(opt.id) ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                    {opt.name}
                                </span>
                            </div>
                            <p className={`text-xs mt-1 ml-7 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{opt.desc}</p>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Step 5: Review & Architecture
const ReviewStep = ({ config, isDarkMode, showArchitecture, isGenerating, architectureRef }) => {
    const primaryZone = AVAILABILITY_ZONES.find(z => z.id === config.primaryZone);
    const drZone = AVAILABILITY_ZONES.find(z => z.id === config.drZone);
    const computeTier = COMPUTE_TIERS.find(t => t.id === config.computeTier);
    const storageType = STORAGE_TYPES.find(t => t.id === config.storageType);

    if (isGenerating) {
        return (
            <div className="py-20 text-center">
                <div className="w-16 h-16 mx-auto mb-6 relative">
                    <div className="absolute inset-0 border-4 border-amber-500/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-amber-500 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Generating Architecture</h3>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Analyzing your requirements and designing optimal configuration...</p>
            </div>
        );
    }

    return (
        <div className="space-y-8" ref={architectureRef}>
            <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Architecture Review
                </h2>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Review your cloud architecture design and recommendations.
                </p>
            </div>

            {/* Architecture Visualization */}
            <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <h3 className={`text-sm font-medium mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Architecture Diagram</h3>
                
                <div className="relative">
                    {/* On-Prem Section */}
                    {config.hasOnPrem && (
                        <div className="flex items-center gap-4 mb-8">
                            <div className={`flex-1 p-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-blue-500/30 bg-blue-500/5' : 'border-blue-300 bg-blue-50'}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <Building2 className="w-5 h-5 text-blue-500" />
                                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>On-Premises Datacenter</span>
                                </div>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                    {config.onPremLocation || 'Customer Location'} • {config.vpnType.toUpperCase()} VPN • {config.bandwidthRequired} Mbps
                                </p>
                                {config.internetRouting === 'onprem' && (
                                    <div className={`mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                                        <Router className="w-3 h-3" /> Internet egress point
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col items-center">
                                <Lock className="w-4 h-4 text-amber-500 mb-1" />
                                <div className={`w-0.5 h-8 ${isDarkMode ? 'bg-amber-500/50' : 'bg-amber-500'}`}></div>
                                <span className="text-[10px] text-amber-500 font-medium">Encrypted</span>
                            </div>
                        </div>
                    )}

                    {/* Cloud Zones */}
                    <div className={`p-6 rounded-xl border-2 ${isDarkMode ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-300 bg-emerald-50'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Cloud className="w-5 h-5 text-emerald-500" />
                                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>EverestCloud Infrastructure</span>
                            </div>
                            {config.internetRouting === 'everestcloud' && (
                                <div className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                                    {config.useFirewall && <ShieldCheck className="w-3 h-3" />}
                                    {config.usePublicIP && <Wifi className="w-3 h-3" />}
                                    <span>Direct Internet</span>
                                </div>
                            )}
                        </div>

                        <div className={`grid ${config.enableDR ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                            {/* Primary Zone */}
                            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-100 border-emerald-300'}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                    <span className="text-emerald-600 dark:text-emerald-400 text-sm font-bold">{primaryZone?.name} ({primaryZone?.code})</span>
                                    <span className="text-[10px] px-2 py-0.5 bg-emerald-500 text-white rounded-full">PRIMARY</span>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-white'} text-center`}>
                                        {config.sizingMode === 'vm' ? (
                                            <>
                                                <Server className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{config.vmCount} VMs</span>
                                            </>
                                        ) : (
                                            <>
                                                <Cpu className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{config.totalVcpu} vCPUs</span>
                                            </>
                                        )}
                                    </div>
                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-white'} text-center`}>
                                        <Database className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                        <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{config.sizingMode === 'vm' ? config.storageSize : config.totalStorage} GB</span>
                                    </div>
                                </div>
                            </div>

                            {/* DR Zone */}
                            {config.enableDR && (
                                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-100 border-amber-300'}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <RefreshCw className="w-4 h-4 text-amber-500" />
                                        <span className="text-amber-600 dark:text-amber-400 text-sm font-bold">{drZone?.name} ({drZone?.code})</span>
                                        <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-slate-900 rounded-full">DR SITE</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-white'} text-center`}>
                                            <Server className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Standby</span>
                                        </div>
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-white'} text-center`}>
                                            <Database className={`w-4 h-4 mx-auto mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                            <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Replicated</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {config.enableDR && (
                            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-amber-500">
                                <GitBranch className="w-3 h-3" />
                                <span>Synchronous Replication • RPO=0 • RTO&lt;15min</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Configuration Summary */}
            <div className="grid grid-cols-2 gap-4">
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <Cpu className="w-4 h-4 text-amber-500" /> Compute
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Sizing Mode</span>
                            <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.sizingMode === 'vm' ? 'VM-Based' : 'Resource-Based'}</span>
                        </div>
                        {config.sizingMode === 'vm' ? (
                            <>
                                <div className="flex justify-between">
                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Tier</span>
                                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{computeTier?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>VMs</span>
                                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.vmCount}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between">
                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Total vCPU</span>
                                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.totalVcpu}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Total Memory</span>
                                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.totalMemory} GB</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <Network className="w-4 h-4 text-amber-500" /> Connectivity
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Internet</span>
                            <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.internetRouting === 'everestcloud' ? 'Direct' : 'Via On-Prem'}</span>
                        </div>
                        {config.hasOnPrem && (
                            <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>VPN</span>
                                <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{config.vpnType.toUpperCase()}</span>
                            </div>
                        )}
                        {config.internetRouting === 'everestcloud' && (
                            <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Services</span>
                                <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>
                                    {[config.usePublicIP && 'Public IP', config.useFirewall && 'Firewall'].filter(Boolean).join(', ') || 'None'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recommendations */}
            <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 text-amber-600 dark:text-amber-400`}>
                    <Zap className="w-4 h-4" /> Recommendations
                </h4>
                <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                            Multi-AZ deployment recommended for high availability and compliance
                        </span>
                    </li>
                    {config.enableDR && (
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                                Synchronous replication enabled for zero data loss (RPO=0)
                            </span>
                        </li>
                    )}
                    {config.hasOnPrem && (
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                                Hybrid connectivity via encrypted {config.vpnType.toUpperCase()} tunnel
                            </span>
                        </li>
                    )}
                    {config.internetRouting === 'everestcloud' && config.useFirewall && (
                        <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                                Managed firewall with L7 WAF and DDoS protection included
                            </span>
                        </li>
                    )}
                    <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                            24/7 managed monitoring and support included
                        </span>
                    </li>
                </ul>
            </div>

            {/* Pricing Note */}
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-white/10' : 'bg-slate-100 border-slate-200'} text-center`}>
                <AlertTriangle className={`w-5 h-5 mx-auto mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    For detailed pricing and implementation timeline, please request a quote. Our team will review your requirements and provide a customized proposal.
                </p>
            </div>
        </div>
    );
};

export default Planner;
